import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useId, useRef, useState } from 'react';
import HoldButton from '@ui/components/vendor/react-bits/HoldButton/HoldButton';
import { microBrand } from './brand';
import './hold-to-confirm.scss';
const SECOND_CLICK_GUARD_MS = 500;
const PENDING_RESET_MS = 4000;
const TAP_MS = 250;
/**
 * Destructive confirmation: hold for 1.5 s (pointer, touch, Space/Enter), or click twice.
 * The click route exists for assistive technology and voice control (WCAG 2.1.1).
 * Presentation only: it calls the parent's existing confirm handler and nothing else.
 */
export const HoldToConfirmButton = ({ label, confirmLabel = 'Click again to confirm', pendingLabel = 'Working…', helperText = 'Press and hold, or click twice', armedHelperText = 'Click again to confirm', onConfirm, holdTime = 1500, disabled = false, fullWidth = true }) => {
    const helperId = useId();
    const [state, setState] = useState('idle');
    const [nudge, setNudge] = useState(false);
    const [announcement, setAnnouncement] = useState('');
    const stateRef = useRef('idle');
    const armedAt = useRef(0);
    const onConfirmRef = useRef(onConfirm);
    onConfirmRef.current = onConfirm;
    const timers = useRef({});
    const setConfirmState = (next) => {
        stateRef.current = next;
        setState(next);
    };
    useEffect(() => {
        const current = timers.current;
        return () => {
            window.clearTimeout(current.nudge);
            window.clearTimeout(current.reset);
        };
    }, []);
    const confirm = () => {
        if (disabled || stateRef.current === 'pending')
            return;
        setConfirmState('pending');
        setAnnouncement(pendingLabel);
        window.clearTimeout(timers.current.reset);
        timers.current.reset = window.setTimeout(() => {
            setConfirmState('idle');
            setAnnouncement('');
        }, PENDING_RESET_MS);
        onConfirmRef.current();
    };
    const handleTap = () => {
        if (disabled)
            return;
        const current = stateRef.current;
        if (current === 'pending')
            return;
        if (current === 'idle') {
            armedAt.current = performance.now();
            setConfirmState('armed');
            setAnnouncement(armedHelperText);
            return;
        }
        if (performance.now() - armedAt.current >= SECOND_CLICK_GUARD_MS)
            confirm();
    };
    const handleRelease = ({ heldMs, drifted }) => {
        if (drifted || heldMs < TAP_MS || stateRef.current === 'pending')
            return;
        setNudge(true);
        window.clearTimeout(timers.current.nudge);
        timers.current.nudge = window.setTimeout(() => setNudge(false), 2400);
    };
    const handleKeyDown = (event) => {
        if (event.key === 'Escape' && stateRef.current === 'armed') {
            setConfirmState('idle');
            setAnnouncement('');
        }
    };
    const armed = state === 'armed';
    const pending = state === 'pending';
    const strong = armed || pending;
    const buttonLabel = pending ? pendingLabel : armed ? confirmLabel : label;
    const seconds = Math.round(holdTime / 100) / 10;
    const helper = pending ? '' : armed ? armedHelperText : nudge ? `Keep holding for ${seconds} s, or click twice` : helperText;
    return (_jsxs("span", { className: `hold-to-confirm${fullWidth ? ' hold-to-confirm--full' : ''}`, "data-state": state, "data-nudge": nudge ? '' : undefined, onKeyDown: handleKeyDown, children: [_jsx(HoldButton, { className: 'hold-to-confirm__button', size: 'md', radius: 999, holdTime: holdTime, releaseTime: 220, pressScale: 0.98, wave: false, glow: false, resetAfter: PENDING_RESET_MS, disabled: disabled, backgroundColor: strong ? microBrand.destructive : microBrand.destructiveSoft, textColor: strong ? microBrand.onDestructive : microBrand.destructive, fillColor: strong ? microBrand.destructiveStrong : microBrand.destructive, fillTextColor: microBrand.onDestructive, doneLabel: pendingLabel, describedBy: helperId, onHold: confirm, onTap: handleTap, onRelease: handleRelease, children: buttonLabel }), _jsx("span", { id: helperId, className: 'hold-to-confirm__helper', children: helper }), _jsx("span", { className: 'hold-to-confirm__sr', "aria-live": 'polite', children: announcement })] }));
};
